/**
 * Thin axios wrapper that consumes the centralised `endpoints` map.
 *
 * Lets call sites skip the per-call `endpoints.foo()` invocation:
 *
 *   // before
 *   import axios from "axios";
 *   import { endpoints } from "../lib/api";
 *   await axios.get(endpoints.groups());
 *   await axios.post(endpoints.prayers(), payload);
 *   await axios.patch(endpoints.group(id), payload);
 *
 *   // after
 *   import { apiClient } from "../lib/apiClient";
 *   await apiClient.get("groups");
 *   await apiClient.post("prayers", payload);
 *   await apiClient.patch("group", id, payload);
 *
 * Path params are positional and come BEFORE the request body. The wrapper
 * inspects `endpoints[name].length` to decide how many leading arguments
 * are path params vs payload.
 *
 * NOTE: do not declare endpoints with default-valued or rest params —
 * `fn.length` ignores them and the wrapper would mis-split path args
 * from the body. Plain positional params only.
 *
 * Adopt opt-in: existing `axios.get(endpoints.foo())` callsites continue
 * to work unchanged.
 */
import axios from "axios";
import { endpoints } from "./api";

const HTTP_VERBS = ["get", "post", "patch", "put", "delete"];

// Dev-mode timing log. CRA wires NODE_ENV → "development" when running `yarn
// start`, → "test" under Jest, → "production" for `yarn build`. We only log in
// development so the production bundle stays silent (and tree-shakers can drop
// the branch via dead-code elimination).
const TIMING_ENABLED = process.env.NODE_ENV === "development";

function logTiming(verb, name, ms, ok) {
  // eslint-disable-next-line no-console
  console.debug(
    `api:${verb} ${name} ${Math.round(ms)}ms${ok ? "" : " [error]"}`,
  );
}

function callEndpoint(verb, name, args) {
  const fn = endpoints[name];
  if (typeof fn !== "function") {
    throw new Error(
      `apiClient: unknown endpoint "${name}". ` +
        "Add it to /app/frontend/src/lib/api.js#endpoints first.",
    );
  }
  const arity = fn.length;
  const pathArgs = args.slice(0, arity);
  const dataOrConfig = args[arity];
  const url = fn(...pathArgs);

  if (!TIMING_ENABLED) {
    return axios[verb](url, dataOrConfig);
  }
  return timed(verb, name, () => axios[verb](url, dataOrConfig));
}

async function timed(verb, name, run) {
  const clock = typeof performance !== "undefined" ? performance : Date;
  const started = clock.now();
  try {
    const result = await run();
    logTiming(verb, name, clock.now() - started, true);
    return result;
  } catch (err) {
    logTiming(verb, name, clock.now() - started, false);
    throw err;
  }
}

export const apiClient = HTTP_VERBS.reduce((acc, verb) => {
  acc[verb] = (name, ...args) => callEndpoint(verb, name, args);
  return acc;
}, {});
